/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'

// Removes focus for non-keyboard interactions for the whole application
import 'focus-visible/dist/focus-visible'

import {MultiSiteProvider} from '@salesforce/retail-react-app/app/contexts'

import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {proxyBasePath} from '@salesforce/pwa-kit-runtime/utils/ssr-namespace-paths'
import {createUrlTemplate} from '@salesforce/retail-react-app/app/utils/url'
import createLogger from '@salesforce/pwa-kit-runtime/utils/logger-factory'
import {Fonts} from '../fonts'
import {CommerceApiProvider} from '@salesforce/commerce-sdk-react'
import {withReactQuery} from '@salesforce/pwa-kit-react-sdk/ssr/universal/components/with-react-query'
import {useCorrelationId} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {ReactQueryDevtools} from '@tanstack/react-query-devtools'
import ChakraCustomProvider from '../../context/chakra'
import {ThemeProvider} from '../../context/theme'
import GigyaProvider from '../../context/gigya'
import {ContentstackProvider, ContentstackProviderRMS} from '../../context/contentstack'
import GoogleTagManagerApi from '../../custom-api/google-tag-manager/google-tag-manager.js'
import {GoogleTagManagerProvider} from '../../custom-api/google-tag-manager/contexts.js'
import {resolveLocaleFromUrl, resolveSiteFromUrl} from '../../utils/site-utils.js'

/**
 * Use the AppConfig component to inject extra arguments into the getProps
 * methods for all Route Components in the app – typically you'd want to do this
 * to inject a connector instance that can be used in all Pages.
 *
 * You can also use the AppConfig to configure a state-management library such
 * as Redux, or Mobx, if you like.
 */
const AppConfig = ({children, locals = {}}) => {
    const {correlationId} = useCorrelationId()
    const headers = {
        'correlation-id': correlationId,
    }

    const commerceApiConfig = locals.appConfig.commerceAPI
    const appOrigin = getAppOrigin()

    return (
        <CommerceApiProvider
            shortCode={commerceApiConfig.parameters.shortCode}
            clientId={commerceApiConfig.parameters.clientId}
            organizationId={commerceApiConfig.parameters.organizationId}
            siteId={locals.site?.id}
            locale={locals.locale?.id}
            currency={locals.locale?.preferredCurrency}
            redirectURI={`${appOrigin}/callback`}
            proxy={`${appOrigin}${commerceApiConfig.proxyPath}`}
            headers={headers}
            OCAPISessionsURL={`${appOrigin}${proxyBasePath}/ocapi/s/${locals.site?.id}/dw/shop/v22_8/sessions`}
            logger={createLogger({packageName: 'commerce-sdk-react'})}
            enablePWAKitPrivateClient={true}
        >
            <MultiSiteProvider site={locals.site} locale={locals.locale} buildUrl={locals.buildUrl}>
                <GoogleTagManagerProvider value={locals.gmt}>
                    <Fonts />
                    <ThemeProvider>
                        <ChakraCustomProvider>
                            <ContentstackProvider>
                                <ContentstackProviderRMS>
                                    <GigyaProvider>{children}</GigyaProvider>
                                </ContentstackProviderRMS>
                            </ContentstackProvider>
                        </ChakraCustomProvider>
                    </ThemeProvider>
                </GoogleTagManagerProvider>
            </MultiSiteProvider>
            <ReactQueryDevtools />
        </CommerceApiProvider>
    )
}

AppConfig.restore = (locals = {}) => {
    const path =
        typeof window === 'undefined'
            ? locals.originalUrl
            : `${window.location.pathname}${window.location.search}`
    const site = resolveSiteFromUrl(path)
    const locale = resolveLocaleFromUrl(path)

    const {app: appConfig, customParams} = getConfig()
    const apiConfig = {
        ...appConfig.commerceAPI,
        einsteinConfig: appConfig.einsteinAPI,
    }

    apiConfig.parameters.siteId = site.id

    locals.buildUrl = createUrlTemplate(appConfig, site.alias || site.id, locale.id)
    locals.site = site
    locals.locale = locale
    locals.appConfig = appConfig

    const {googleTagManagerId, googleTagManagerAuthToken, googleTagManagerEnvNum} =
        customParams || ''
    locals.gmt = new GoogleTagManagerApi({
        id: googleTagManagerId,
        authToken: googleTagManagerAuthToken,
        envNum: googleTagManagerEnvNum,
    })
}

AppConfig.freeze = () => undefined

AppConfig.extraGetPropsArgs = (locals = {}) => {
    return {
        buildUrl: locals.buildUrl,
        site: locals.site,
        locale: locals.locale,
    }
}

AppConfig.propTypes = {
    children: PropTypes.node,
    locals: PropTypes.object,
}

const isServerSide = typeof window === 'undefined'

// Recommended settings for PWA-Kit usages.
// NOTE: they will be applied on both server and client side.
// retry is always disabled on server side regardless of the value from the options
const options = {
    queryClientConfig: {
        defaultOptions: {
            queries: {
                retry: false,
                refetchOnWindowFocus: false,
                staleTime: 10 * 1000,
                ...(isServerSide ? {retryOnMount: false} : {}),
            },
            mutations: {
                retry: false,
            },
        },
    },
}

export default withReactQuery(AppConfig, options)
